import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePDF } from '../context/PDFContext';
import { useNavigate } from 'react-router-dom';
import { startPaystackCheckout } from '../lib/paystackCheckout';
import { Check, Zap, FileDown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PricingPage = () => {
  const { user, refreshUser } = useAuth();
  const { refreshLock } = usePDF();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const isPro = user?.is_pro || user?.plan === 'lifetime' || user?.plan === 'monthly' || user?.is_staff;

  const handlePurchase = async (planId) => {
    if (!user) {
      toast.info('Sign in first to purchase');
      navigate('/login');
      return;
    }
    if (user?.is_staff) {
      toast.info("You have staff access — no payment needed");
      return;
    }
    if (planId === 'subscription' && isPro) {
      toast.info("You're already subscribed!");
      return;
    }
    setLoadingPlan(planId);
    try {
      const result = await startPaystackCheckout(planId, {
        onPaid: async () => {
          await refreshLock();
          refreshUser();
        },
      });
      if (result === 'paid') {
        toast.success('Payment confirmed!');
      } else if (result === 'failed') {
        toast.error('Payment was not completed. Please try again.');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4" data-testid="pricing-title">
            Simple, Fair Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Start free with 1 download per day. Unlock a single document, or go unlimited.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="border border-border rounded-xl p-6 bg-card" data-testid="plan-free">
            <h3 className="text-lg font-semibold mb-1">Free</h3>
            <p className="text-3xl font-bold mb-1">R0</p>
            <p className="text-sm text-muted-foreground mb-6">forever</p>
            <ul className="space-y-3 text-sm mb-8">
              {['All PDF tools', '1 download per day', 'Up to 20MB files'].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full" onClick={() => navigate('/editor')} data-testid="plan-free-btn">
              Get Started
            </Button>
          </div>

          {/* Single Use */}
          <div className="border border-border rounded-xl p-6 bg-card" data-testid="plan-single">
            <h3 className="text-lg font-semibold mb-1">Single Use</h3>
            <p className="text-3xl font-bold mb-1">R8</p>
            <p className="text-sm text-muted-foreground mb-6">one document download</p>
            <ul className="space-y-3 text-sm mb-8">
              {['All PDF tools', '1 extra document download', 'No subscription needed', 'Credits never expire'].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handlePurchase('single')}
              disabled={loadingPlan === 'single'}
              data-testid="plan-single-btn"
            >
              {loadingPlan === 'single' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
              Buy 1 Download
            </Button>
            {user?.remaining_downloads > 0 && (
              <p className="text-xs text-center text-muted-foreground mt-3" data-testid="credits-remaining">
                You have {user.remaining_downloads} unused download{user.remaining_downloads > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Subscription */}
          <div className="border-2 border-primary rounded-xl p-6 bg-card relative" data-testid="plan-subscription">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
              3-DAY FREE TRIAL
            </div>
            <h3 className="text-lg font-semibold mb-1">Pro Unlimited</h3>
            <p className="text-3xl font-bold mb-1">R49<span className="text-base font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mb-6">R1 card check today · billing starts after 3 days · cancel anytime</p>
            <ul className="space-y-3 text-sm mb-8">
              {['All PDF tools', 'Unlimited downloads', 'Priority processing', 'No ads', '3-day free trial'].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full"
              onClick={() => handlePurchase('subscription')}
              disabled={loadingPlan === 'subscription' || isPro}
              data-testid="plan-subscription-btn"
            >
              {loadingPlan === 'subscription' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              {isPro ? 'Current Plan' : 'Start Free Trial'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
